/**
 * App.tsx — Mission Control shell (glassmorphism edition)
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │  AppHeader: logo · run-id · tenant · status · avatar │
 *   ├──────────┬──────────────────────────────────────────┤
 *   │NavSidebar│  RunHistorySidebar │ main content area    │
 *   │(animated)│  (run list)        │ tabs + view          │
 *   └──────────┴──────────────────────────────────────────┘
 *
 * New in this revision:
 *   • AppHeader replaces the inline Header (glassmorphism, status dot, avatar)
 *   • NavSidebar replaces the inline Sidebar (Framer Motion collapse animation)
 *   • ActiveRunsTable appears above the main area with live-polling runs
 *   • ApprovalModal handles waiting_for_approval gates
 *   • Glassmorphism tokens from src/styles/tokens.css
 *   • Self-hosted Instrument Sans + IBM Plex Mono via src/styles/fonts.css
 */

import { useState, useEffect, useMemo, type KeyboardEvent } from 'react'

// ── Styles ────────────────────────────────────────────────────────────────────
// (fonts.css + klaxiora.css are foundation-level and load from main.tsx.)
import './styles/tokens.css'
import './App.css'

// ── Existing components (unchanged) ──────────────────────────────────────────
import { DAGCanvas }           from './components/DAGCanvas'
import { ObservabilityPage }   from './components/ObservabilityPage'
import { IntegrationsPage }    from './components/IntegrationsPage'
import { LinkedInConnectionCard } from './components/LinkedInConnectionCard'
import { RunHistorySidebar }   from './components/RunHistorySidebar'
import { AuditLogView }        from './components/AuditLogView'
import { ScrapingHealthPanel } from './components/ScrapingHealthPanel'
import { AgentsPage }          from './components/AgentsPage'
import { HomePage }            from './components/HomePage'
import { MarketplacePage }     from './components/MarketplacePage'
import { WorkflowBuilder }     from './components/WorkflowBuilder'
import { Dashboard }           from './components/dashboard/Dashboard'
import { dealIdFromPath }      from './components/dashboard/pages/sales/dealLink'
import { Toaster }             from 'sonner'

// ── New components ────────────────────────────────────────────────────────────
import { AppHeader }           from './components/layout/AppHeader'
import { NavSidebar }          from './components/layout/NavSidebar'
import { ActiveRunsTable }     from './features/runs/ActiveRunsTable'
import { ApprovalModal }       from './components/common/ApprovalModal'
import { GlobalSearch }        from './components/common/GlobalSearch'

// ── Contexts / API ────────────────────────────────────────────────────────────
import { useTenant }           from './contexts/TenantContext'
import { SessionWriteAccess }  from './lib/WriteAccessProvider'
import { api, getToken, isStubAuth, isWorkOSAuth, clearToken } from './api/client'
import { LoginPage }           from './components/auth/LoginPage'
import { signOut as workosSignOut } from './auth/workosAuth'
import type { ApprovalGate, ApprovalDecision } from './api/types'
import type { Department }     from './components/layout/NavSidebar'
import { APP_MODE_KEY, applyTheme, clearTheme, getTheme } from './theme/theme'

// ── Sidebar data ──────────────────────────────────────────────────────────────

const DEPARTMENTS: Department[] = [
  {
    id: 'research', label: 'Research',
    skills: [
      { id: 'market-researcher',   label: 'Market Researcher'  },
      { id: 'market-librarian',    label: 'Market Librarian'   },
      { id: 'market-summarizer',   label: 'Market Summarizer'  },
    ],
  },
  {
    id: 'recruitment', label: 'Recruitment',
    skills: [
      { id: 'recruitment', label: 'Job Search' },
    ],
  },
  {
    id: 'hr', label: 'HR',
    skills: [
      { id: 'hr-screener',     label: 'CV Screener'       },
      { id: 'hr-onboarding',   label: 'Onboarding Guide'  },
    ],
  },
  {
    id: 'legal', label: 'Legal',
    skills: [
      { id: 'legal-reviewer',   label: 'Contract Reviewer' },
      { id: 'legal-summarizer', label: 'Clause Summarizer' },
    ],
  },
  {
    id: 'finance', label: 'Finance',
    skills: [
      { id: 'finance-analyst',    label: 'Cost Analyst' },
      { id: 'finance-forecaster', label: 'Forecaster'   },
    ],
  },
  {
    id: 'sales', label: 'Sales',
    skills: [
      { id: 'sales-prospector', label: 'Prospector'     },
      { id: 'sales-closer',     label: 'Deal Qualifier' },
    ],
  },
  {
    id: 'ops', label: 'Operations',
    skills: [
      { id: 'ops-coordinator', label: 'Coordinator' },
      { id: 'ops-scheduler',   label: 'Scheduler'   },
    ],
  },
  {
    id: 'tech', label: 'Engineering',
    skills: [
      { id: 'tech-architect', label: 'Architect'     },
      { id: 'tech-reviewer',  label: 'Code Reviewer' },
    ],
  },
  {
    id: 'product', label: 'Product',
    skills: [
      { id: 'product-strategist', label: 'Strategist'     },
      { id: 'product-researcher', label: 'User Researcher' },
    ],
  },
]

// ── EmptyState ────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[320px] gap-4 text-text-muted text-center" role="main">
      <div className="text-[40px] leading-none opacity-40" aria-hidden="true">⬡</div>
      <h1 className="text-[16px] font-bold text-[var(--text-secondary)] m-0">No run selected</h1>
      <p className="text-[13px] text-text-muted m-0 max-w-[340px] leading-[1.6]">
        Click a run in the table above, or paste a run ID into the header to
        load the live logic map, step traces, token usage, and any pending
        approval gates.
      </p>
      <div className="font-mono text-[11px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-[var(--radius-sm)] px-[14px] py-[6px] text-[var(--text-secondary)]">
        run: &lt;paste run ID above&gt;
      </div>
    </div>
  )
}

// ── ViewTabs ──────────────────────────────────────────────────────────────────

type View = 'home' | 'build' | 'dag' | 'observability' | 'integrations' | 'audit' | 'connections' | 'health' | 'agents' | 'marketplace'

/** Top-level app mode. The Closr Dashboard and Mission Control are fully
 *  separate shells; the choice is persisted to localStorage. */
type AppMode = 'dashboard' | 'mission-control'
// APP_MODE_KEY lives in theme/theme.ts: the theme effect below and index.html's
// pre-paint guard both key off the same value, so it has exactly one home.

function readInitialMode(): AppMode {
  // Deal detail slice 9 (R9): a deal's address is the Dashboard's — a link to
  // one opens it there whatever mode was last stored (the stored choice is
  // left as it was; this load only).
  if (dealIdFromPath(window.location.pathname) !== null) return 'dashboard'
  if (typeof localStorage === 'undefined') return 'dashboard'
  return localStorage.getItem(APP_MODE_KEY) === 'mission-control' ? 'mission-control' : 'dashboard'
}

function ViewTabs({ active, onChange }: { active: View; onChange: (v: View) => void }) {
  const tabs: { id: View; label: string; icon: string }[] = [
    { id: 'home',          label: 'Home',            icon: '⌂' },
    { id: 'build',         label: 'Build',           icon: '✛' },
    { id: 'dag',           label: 'DAG Canvas',      icon: '⬡' },
    { id: 'observability', label: 'Run Details',      icon: '◎' },
    { id: 'audit',         label: 'Audit Log',        icon: '☰' },
    { id: 'integrations',  label: 'Integrations',     icon: '⟳' },
    { id: 'connections',   label: 'Connections',      icon: '⟢' },
    { id: 'health',        label: 'Scraping Health',  icon: '◈' },
    { id: 'agents',        label: 'Agents',           icon: '✦' },
    { id: 'marketplace',   label: 'Marketplace',      icon: '◳' },
  ]
  return (
    <div
      className="flex gap-1 mb-[18px] bg-[var(--bg-glass)] backdrop-blur-[var(--glass-blur)] [-webkit-backdrop-filter:var(--glass-blur)] border-[var(--glass-border)] rounded-[var(--radius-md)] p-1 w-fit"
      role="tablist"
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={tab.id === active}
          className={[
            'flex items-center gap-[7px] px-[14px] py-[6px] rounded-[var(--radius-sm)] border-none cursor-pointer text-[12px] font-bold font-[var(--font-sans)] transition-colors duration-[120ms]',
            tab.id === active
              ? 'bg-[var(--accent-dim)] text-[var(--accent)]'
              : 'bg-transparent text-text-muted hover:bg-[var(--bg-hover)] hover:text-[var(--text-secondary)]',
          ].join(' ')}
          onClick={() => onChange(tab.id)}
          onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter' || e.key === ' ') onChange(tab.id)
          }}
        >
          <span className="text-[11px]" aria-hidden="true">{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  )
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [runId,        setRunId]        = useState('')
  const [activeSkillId,setActiveSkillId]= useState<string | null>(null)
  const [view,         setView]         = useState<View>('home')
  const [sidebarOpen,  setSidebarOpen]  = useState(true)
  const [mode,         setMode]         = useState<AppMode>(readInitialMode)

  // ── Auth gate ──────────────────────────────────────────────────────────────
  // In jwt mode the user must paste a token before the dashboard loads. In stub
  // mode (local dev) the api client uses the dev-owner fallback, so we skip it.
  const [authToken, setAuthToken] = useState<string | null>(getToken)
  function handleLogout() {
    if (isWorkOSAuth()) {
      void workosSignOut()
      return
    }
    clearToken()
    setAuthToken(null)
  }

  // Persist the selected mode so a refresh returns to the same shell.
  useEffect(() => {
    try { localStorage.setItem(APP_MODE_KEY, mode) } catch { /* storage unavailable — non-fatal */ }
  }, [mode])

  // ── THE PORTAL RULE (see theme/theme.ts) ───────────────────────────────────
  // Stamp data-theme on <html> so every subtree — including the drawers and
  // modals that portal into document.body, outside the shell — inherits the
  // tokens AND color-scheme from :root. This is what let the 13 inline
  // colorScheme:'dark' declarations be deleted; a grep fence keeps them gone.
  //
  // MC GUARD: mission-control mode REMOVES the attribute, so no rule in
  // klaxiora.css matches and Mission Control renders exactly as before while
  // it is unmigrated. index.html's pre-paint script does the same read before
  // first paint, so this effect only ever confirms or flips it.
  useEffect(() => {
    if (mode === 'dashboard') applyTheme(getTheme())
    else clearTheme()
  }, [mode])

  // Approval modal state (gates surface via polling or DAGCanvas events)
  const [approvalGate,  setApprovalGate]  = useState<ApprovalGate | null>(null)
  const [approvalOpen,  setApprovalOpen]  = useState(false)
  const [approvalBusy,  setApprovalBusy]  = useState(false)

  // Global search (Cmd+K / Ctrl+K) — MISSION CONTROL ONLY. The palette
  // (<GlobalSearch>) renders in the mission-control branch alone; in dashboard
  // mode the listener used to set state nothing rendered (a dead shortcut on
  // all 50 pages, inventory ⌘K probe). Redesign slice 2b (ADOPTION.md §13.5
  // (d), RULED 2026-09-27): the shortcut leaves dashboard mode with the
  // capsule; it returns with honest search (register row 63).
  const [searchOpen, setSearchOpen] = useState(false)
  useEffect(() => {
    if (mode !== 'mission-control') return
    function onKeyDown(e: KeyboardEvent | globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown as EventListener)
    return () => window.removeEventListener('keydown', onKeyDown as EventListener)
  }, [mode])

  // Expose opener so child components can trigger the modal
  const openApproval = (gate: ApprovalGate) => { setApprovalGate(gate); setApprovalOpen(true) }
  void openApproval  // reserved for child component usage

  const { tenantId, setTenantId } = useTenant()

  // Pre-populated auth headers forwarded to components that call fetch directly.
  // Memoized so its identity only changes when the captured values could change
  // (tenant switch / login) — an unstable identity churns downstream fetch
  // effects (e.g. ObservabilityPage re-hydration) on every App render.
  const hydrateInit: RequestInit = useMemo(
    () => ({ headers: api.headers() }),
    [tenantId, authToken],
  )

  // ── Approval handlers ────────────────────────────────────────────────────
  function dismissApproval() {
    if (approvalBusy) return
    setApprovalOpen(false)
    setApprovalGate(null)
  }

  async function submitApproval(gateId: string, decision: ApprovalDecision, notes?: string) {
    if (!approvalGate) return
    setApprovalBusy(true)
    try {
      await api.post(
        `/v1/runs/${approvalGate.runId}/approvals/${gateId}`,
        { decision, notes },
      )
      dismissApproval()
    } catch (err) {
      console.error('Approval submission failed:', err)
    } finally {
      setApprovalBusy(false)
    }
  }

  // ── Render: auth gate ──────────────────────────────────────────────────────
  // Placed after all hooks so the rules-of-hooks ordering is preserved.
  if (!isStubAuth() && !isWorkOSAuth() && !authToken) {
    return <LoginPage onAuthenticated={setAuthToken} />
  }

  // ── Render: Closr Dashboard mode (full-screen, own shell) ──────────────────
  if (mode === 'dashboard') {
    return (
      <>
        <Toaster position="top-right" richColors />
        <Dashboard onSwitchMode={() => setMode('mission-control')} onLogout={handleLogout} />
      </>
    )
  }

  // ── Render: Mission Control mode (existing shell, unchanged) ───────────────
  // Write controls read the caller's membership role from ONE GET /v1/me per
  // tenant (lib/writeAccess); until it answers — or if it fails — every
  // write control stays hidden.
  return (
    <SessionWriteAccess tenantKey={tenantId}>
    <div className="app-shell">
      <Toaster position="top-right" richColors />
      {/* ── Header ── */}
      <AppHeader
        runId={runId}
        onRunIdChange={setRunId}
        tenantId={tenantId}
        onTenantChange={setTenantId}
        showTenantSwitcher={!isWorkOSAuth()}
        apiStatus="online"
        onSearchOpen={() => setSearchOpen(true)}
        onReviewRun={(id) => { setRunId(id); setView('dag') }}
        onSwitchToDashboard={() => setMode('dashboard')}
        onLogout={isStubAuth() ? undefined : handleLogout}
      />

      {/* ── NavSidebar ── */}
      <NavSidebar
        departments={DEPARTMENTS}
        activeSkillId={activeSkillId}
        onSelectSkill={setActiveSkillId}
        expanded={sidebarOpen}
        onToggleExpanded={() => setSidebarOpen((v) => !v)}
      />

      {/* ── Main ── */}
      <main className="app-main">
        {/* Run history panel */}
        <RunHistorySidebar
          activeRunId={runId}
          onSelectRun={setRunId}
        />

        {/* Content area */}
        <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 0, padding: 0 }}>

          {/* Active runs table — always visible */}
          <div className="px-7 pt-5 border-b border-[var(--border)] bg-[var(--bg-base)]">
            <ActiveRunsTable
              tenantId={tenantId}
              activeRunId={runId}
              onSelectRun={setRunId}
            />
          </div>

          {/* View area */}
          <div style={{ flex: 1, padding: '20px 28px', minHeight: 0, overflow: 'auto' }}>
            <ViewTabs active={view} onChange={setView} />

            {/* Home — unified overview; clicking a run jumps to the DAG Canvas */}
            {view === 'home' && (
              <HomePage
                key={`home:${tenantId}`}
                tenantId={tenantId}
                onOpenRun={(id) => { setRunId(id); setView('dag') }}
                onOpenHealth={() => setView('health')}
              />
            )}

            {/* Build — visual workflow composer (no runId needed) */}
            {view === 'build' && (
              <WorkflowBuilder key={`build:${tenantId}`} />
            )}

            {/* Integrations — never needs a runId */}
            {view === 'integrations' && (
              <IntegrationsPage key={`integrations:${tenantId}`} />
            )}

            {/* Scraping health — per-tenant LinkedIn success rates */}
            {view === 'health' && (
              <ScrapingHealthPanel key={`health:${tenantId}`} />
            )}

            {/* Agents — list + create + edit (no runId needed) */}
            {view === 'agents' && (
              <AgentsPage key={`agents:${tenantId}`} />
            )}

            {/* Marketplace — browse pre-built skills (no runId needed) */}
            {view === 'marketplace' && (
              <MarketplacePage key={`marketplace:${tenantId}`} />
            )}

            {/* Connections — LinkedIn session cookie management */}
            {view === 'connections' && (
              <div className="pt-2">
                <div className="mb-7">
                  <h1 className="text-[22px] font-[900] text-[#dce9f7] m-0 mb-2 tracking-[-0.03em]">Connections</h1>
                  <p className="text-[13px] text-[#4e6480] leading-[1.65] m-0 max-w-[520px]">
                    Manage authenticated sessions for services that require browser-level
                    access. Cookies are encrypted at rest and injected into the
                    Playwright rendering pipeline at scrape time.
                  </p>
                </div>
                <LinkedInConnectionCard key={`connections:${tenantId}`} />
              </div>
            )}

            {/* Run-scoped views */}
            {view !== 'home' && view !== 'build' && view !== 'integrations' && view !== 'connections' && view !== 'health' && view !== 'agents' && view !== 'marketplace' && (
              runId ? (
                <>
                  {view === 'dag' && (
                    <DAGCanvas
                      key={`dag:${tenantId}:${runId}`}
                      runId={runId}
                      height={580}
                    />
                  )}
                  {view === 'observability' && (
                    <ObservabilityPage
                      key={`obs:${tenantId}:${runId}`}
                      runId={runId}
                      hydrateInit={hydrateInit}
                    />
                  )}
                  {view === 'audit' && (
                    <div
                      className="border border-[var(--border)] rounded-[var(--radius-md)] overflow-hidden bg-[var(--bg-surface)] min-h-[400px]"
                      style={{ height: 'calc(100vh - var(--header-h) - 24px - 24px - 44px)' }}
                    >
                      <AuditLogView
                        key={`audit:${tenantId}:${runId}`}
                        runId={runId}
                      />
                    </div>
                  )}
                </>
              ) : (
                <EmptyState />
              )
            )}
          </div>
        </div>
      </main>

      {/* ── Approval modal (portal at app root) ── */}
      <ApprovalModal
        gate={approvalGate}
        open={approvalOpen}
        onDismiss={dismissApproval}
        onDecision={submitApproval}
        submitting={approvalBusy}
      />

      {/* ── Global search (Cmd+K) ── */}
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelectRun={setRunId}
        departments={DEPARTMENTS.map((d) => ({ id: d.id, label: d.label }))}
      />
    </div>
    </SessionWriteAccess>
  )
}

// Export openApproval helper for child components that need to trigger the modal.
// In a larger app this would live in a context; here a module-level ref suffices
// since there's one App instance. Components can import and call it directly.
// (See ApprovalReviewPanel.tsx for usage.)
export type { ApprovalGate }
